import { useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import Page from "src/layouts/Page";
import { ConfirmationModal } from "src/modals/ConfirmationModal/ConfirmationModal";
import RegionForm from "../../components/RegionForm/RegionForm";
import useAdminRegions from "../../hooks/useAdminRegions";
import useEditRegion from "../../hooks/useEditRegion";
import useDeleteRegion from "../../hooks/useDeleteRegion";
import useRetailUsers from "../../hooks/useRetailUsers";
import { adaptRegionPayload } from "../../adapters/adaptRegionPayload";

const BACK_PATH = "/users/locations";

const EMPTY_DEFAULTS: RegionFormValues = {
	name: "",
	regionManager: ""
};

function regionToFormValues(region: Region): RegionFormValues {
	return {
		name: region.name,
		regionManager: region.regionManager ? String(region.regionManager.id) : ""
	};
}

const EditRegion = () => {
	const navigate = useNavigate();
	const { regionId } = useParams<{ regionId: string }>();
	const id = Number(regionId);
	const isValidId = Number.isInteger(id) && id > 0;

	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [defaultValues, setDefaultValues] =
		useState<RegionFormValues>(EMPTY_DEFAULTS);

	const { data: regions = [] } = useAdminRegions();
	const { mutate: editRegion, isLoading: isEditing } = useEditRegion();
	const { mutate: deleteRegion, isLoading: isDeleting } = useDeleteRegion();
	const { data: retailUsers = [] } = useRetailUsers();

	const region = regions.find((r) => r.id === id);

	useEffect(() => {
		if (region) {
			setDefaultValues(regionToFormValues(region));
		}
	}, [region]);

	if (!isValidId) {
		return <Navigate to={BACK_PATH} replace />;
	}

	const handleSubmit = (values: RegionFormValues) => {
		const payload = adaptRegionPayload(values);

		editRegion(
			{ id, payload },
			{
				onSuccess: () => navigate(BACK_PATH)
			}
		);
	};

	const handleDelete = () => {
		deleteRegion(id, {
			onSuccess: () => {
				setShowDeleteModal(false);
				navigate(BACK_PATH);
			}
		});
	};

	return (
		<Page>
			<RegionForm
				defaultValues={defaultValues}
				onSubmit={handleSubmit}
				isSubmitting={isEditing}
				retailUsers={retailUsers}
				backPath={BACK_PATH}
				isEdit
				onDelete={() => setShowDeleteModal(true)}
			/>

			<ConfirmationModal
				isOpen={showDeleteModal}
				title="Delete region?"
				description="This action cannot be undone."
				onCancel={() => setShowDeleteModal(false)}
				onSubmit={handleDelete}
				submitBtnProps={{ isLoading: isDeleting }}
			/>
		</Page>
	);
};

export default EditRegion;
